/**
 * Role Master > Profiles: upload the Word / Excel role profiles, resolve the ones
 * the importer could not place, and see which roles have an approved profile.
 *
 * Uploads never publish anything: a matched profile becomes a DRAFT for its role
 * and waits for approval like any other edit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle, CheckCircle2, FileText, Loader2, RefreshCw, Search, Upload, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import roleProfileService, {
  type ProfileImport, type ProfileState, type ProfileSummary, type ProfileSummaryRow,
} from '@/services/api/roleProfileService';
import { RoleProfileDialog, apiError } from '@/components/role-profiles/RoleProfileDialog';

const UPLOAD_CHUNK = 10;

const STATE_BADGE: Record<ProfileState, { label: string; cls: string }> = {
  none: { label: 'No profile', cls: 'bg-gray-100 text-gray-600' },
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800' },
  submitted: { label: 'Awaiting approval', cls: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  approved_with_draft: { label: 'Approved + draft', cls: 'bg-emerald-100 text-emerald-800' },
};

type Filter = 'all' | 'none' | 'in_progress' | 'submitted' | 'approved' | 'not_ready' | 'review_due';
const FILTERS: { key: Filter; label: string; test: (r: ProfileSummaryRow) => boolean }[] = [
  { key: 'all', label: 'All roles', test: () => true },
  { key: 'none', label: 'No profile', test: (r) => r.state === 'none' },
  { key: 'in_progress', label: 'Draft in progress', test: (r) => !!r.open_version },
  { key: 'submitted', label: 'Awaiting approval', test: (r) => r.open_status === 'submitted' },
  { key: 'approved', label: 'Approved', test: (r) => !!r.approved_version },
  { key: 'not_ready', label: 'Not ready for Neptune', test: (r) => !r.neptune_ready },
  { key: 'review_due', label: 'Review due', test: (r) => r.review_due },
];

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// -- review queue row --------------------------------------------------------------------------------
const QueueRow: React.FC<{
  item: ProfileImport; roles: ProfileSummaryRow[]; onDone: () => void;
}> = ({ item, roles, onDone }) => {
  const [roleId, setRoleId] = useState<string>(
    item.role_master_entry_id ? String(item.role_master_entry_id)
      : item.candidates[0] && item.candidates[0].score >= 0.8 ? String(item.candidates[0].role_id) : '',
  );
  const [busy, setBusy] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const suggested = new Set(item.candidates.map((c) => c.role_id));

  const apply = async () => {
    setBusy(true);
    try {
      await roleProfileService.applyImport(item.id, Number(roleId), item.status === 'conflict');
      toast.success('Draft created from the upload');
      onDone();
    } catch (e) {
      toast.error(apiError(e, 'Could not apply the upload.'));
    } finally {
      setBusy(false);
    }
  };
  const discard = async () => {
    setBusy(true);
    try {
      await roleProfileService.discardImport(item.id);
      onDone();
    } catch (e) {
      toast.error(apiError(e, 'Could not discard the upload.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow className="align-top">
      <TableCell className="max-w-[240px]">
        <div className="truncate font-medium" title={item.filename}>{item.filename}</div>
        {item.sheet_name && <div className="text-xs text-gray-500">Sheet: {item.sheet_name}</div>}
        <div className="text-xs text-gray-500">{fmtDate(item.created_at)}</div>
      </TableCell>
      <TableCell className="max-w-[200px]">
        <div className="truncate" title={item.extracted_title ?? ''}>{item.extracted_title || <span className="italic text-gray-400">not named</span>}</div>
        {item.completeness && <div className="text-xs text-gray-500">{item.completeness.score}% complete</div>}
      </TableCell>
      <TableCell className="max-w-[320px] text-sm">
        {item.status === 'failed'
          ? <span className="text-red-700">{item.error}</span>
          : <span className={item.status === 'conflict' ? 'text-amber-800' : 'text-gray-700'}>{item.match_reason}</span>}
        {item.warnings.length > 0 && (
          <div>
            <button type="button" className="text-xs text-amber-700 underline" onClick={() => setShowWarnings((v) => !v)}>
              {item.warnings.length} note{item.warnings.length > 1 ? 's' : ''} from the reader
            </button>
            {showWarnings && <ul className="mt-1 list-disc pl-4 text-xs text-gray-600">{item.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          </div>
        )}
      </TableCell>
      <TableCell className="min-w-[260px]">
        {item.status !== 'failed' && (
          <select className="h-9 w-full rounded-md border bg-white px-2 text-sm" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">Choose the role…</option>
            {item.candidates.length > 0 && (
              <optgroup label="Suggested">
                {item.candidates.map((c) => (
                  <option key={c.role_id} value={c.role_id}>{c.title} ({c.role_code}){c.department ? `, ${c.department}` : ''}</option>
                ))}
              </optgroup>
            )}
            <optgroup label="All roles">
              {roles.filter((r) => !suggested.has(r.role_id)).map((r) => (
                <option key={r.role_id} value={r.role_id}>{r.title} ({r.role_code})</option>
              ))}
            </optgroup>
          </select>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right">
        {item.status !== 'failed' && (
          <Button size="sm" onClick={apply} disabled={!roleId || busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : item.status === 'conflict' ? 'Replace draft' : 'Create draft'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={discard} disabled={busy}>Discard</Button>
      </TableCell>
    </TableRow>
  );
};

// -- the tab ---------------------------------------------------------------------------------------------
export const RoleProfilesTab: React.FC = () => {
  const [summary, setSummary] = useState<ProfileSummary | null>(null);
  const [queue, setQueue] = useState<ProfileImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [dialogRole, setDialogRole] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        roleProfileService.getSummary(),
        roleProfileService.listImports(['needs_role', 'conflict', 'failed']),
      ]);
      setSummary(s);
      setQueue(q.items);
    } catch (e) {
      toast.error(apiError(e, 'Could not load role profiles.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    const batchId = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`).slice(0, 36);
    const totals = { applied: 0, needs_role: 0, conflict: 0, failed: 0 };
    setUploading({ done: 0, total: list.length });
    try {
      for (let i = 0; i < list.length; i += UPLOAD_CHUNK) {
        const chunk = list.slice(i, i + UPLOAD_CHUNK);
        const res = await roleProfileService.uploadProfiles(chunk, batchId);
        (Object.keys(totals) as (keyof typeof totals)[]).forEach((k) => { totals[k] += res.counts[k] ?? 0; });
        setUploading({ done: Math.min(i + UPLOAD_CHUNK, list.length), total: list.length });
      }
      const waiting = totals.needs_role + totals.conflict;
      toast.success(
        `${totals.applied} draft${totals.applied === 1 ? '' : 's'} created` +
        (waiting ? `, ${waiting} waiting for you to choose the role` : '') +
        (totals.failed ? `, ${totals.failed} could not be read` : ''),
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(apiError(e, 'The upload stopped part-way. Files already read are kept.'));
    } finally {
      setUploading(null);
      if (fileInput.current) fileInput.current.value = '';
      load();
    }
  };

  const rows = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter)!;
    const q = search.trim().toLowerCase();
    return (summary?.rows ?? []).filter((r) => f.test(r) && (!q
      || r.title.toLowerCase().includes(q) || r.role_code.toLowerCase().includes(q)
      || (r.department ?? '').toLowerCase().includes(q) || (r.function ?? '').toLowerCase().includes(q)));
  }, [summary, filter, search]);

  const t = summary?.totals;
  const tiles: { label: string; value: number | undefined; tone: string; filter: Filter }[] = [
    { label: 'Roles', value: t?.roles, tone: 'text-gray-900', filter: 'all' },
    { label: 'Approved', value: t?.approved, tone: 'text-emerald-700', filter: 'approved' },
    { label: 'Awaiting approval', value: t?.submitted, tone: 'text-blue-700', filter: 'submitted' },
    { label: 'Drafts in progress', value: t?.in_progress, tone: 'text-amber-700', filter: 'in_progress' },
    { label: 'No profile', value: t?.none, tone: 'text-gray-600', filter: 'none' },
    { label: 'Ready for Neptune', value: t?.neptune_ready, tone: 'text-emerald-700', filter: 'approved' },
    { label: 'Review due', value: t?.review_due, tone: 'text-red-700', filter: 'review_due' },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Role profiles</CardTitle>
              <p className="mt-1 max-w-3xl text-sm text-gray-600">
                One profile per role, kept here instead of in Word. Upload existing profiles in the Word (.doc, .docx)
                or Excel (.xlsx) template: each one is matched to its role and becomes a draft. Nothing goes live until
                it is approved, and only an approved profile feeds Neptune and printed profiles.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileInput} type="file" multiple accept=".doc,.docx,.xlsx" className="hidden"
                onChange={(e) => onFiles(e.target.files)} />
              <Button onClick={() => fileInput.current?.click()} disabled={!!uploading}>
                {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                {uploading ? `Reading ${uploading.done} of ${uploading.total}…` : 'Upload profiles'}
              </Button>
              <Button variant="outline" size="icon" onClick={load} disabled={loading} aria-label="Refresh">
                <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              </Button>
            </div>
          </div>
          {uploading && <Progress className="mt-2 h-1.5" value={(uploading.done / uploading.total) * 100} />}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {tiles.map((tile) => (
              <button key={tile.label} type="button" onClick={() => setFilter(tile.filter)}
                className={cn('rounded-lg border px-3 py-2 text-left transition hover:bg-gray-50',
                  filter === tile.filter && tile.label !== 'Ready for Neptune' && 'border-heritage-blue ring-1 ring-heritage-blue')}>
                <div className={cn('text-xl font-semibold', tile.tone)}>{tile.value ?? '—'}</div>
                <div className="text-xs text-gray-500">{tile.label}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {queue.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Uploads waiting for you ({queue.length})
            </CardTitle>
            <p className="text-sm text-gray-600">
              The reader could not place these by itself. Choose the role and create the draft, or discard the upload.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Role named in the file</TableHead>
                  <TableHead>Why it is waiting</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((item) => (
                  <QueueRow key={item.id} item={item} roles={summary?.rows ?? []} onDone={load} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'} onClick={() => setFilter(f.key)}>
                {f.label}
              </Button>
            ))}
            <div className="relative ml-auto min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input className="pl-8" placeholder="Search role, code or department" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !summary ? (
            <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Function / department</TableHead>
                  <TableHead className="text-right">Seats</TableHead>
                  <TableHead>Profile</TableHead>
                  <TableHead className="w-[170px]">Complete</TableHead>
                  <TableHead>Neptune</TableHead>
                  <TableHead>Reviewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.role_id} className="cursor-pointer hover:bg-gray-50" onClick={() => setDialogRole(r.role_id)}>
                    <TableCell>
                      <div className="font-medium text-gray-900">{r.title}</div>
                      <div className="font-mono text-xs text-gray-500">{r.role_code}{r.grade ? ` · ${r.grade}` : ''}</div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{[r.function, r.department].filter(Boolean).join(' / ') || '—'}</TableCell>
                    <TableCell className="text-right text-sm">{r.seats}</TableCell>
                    <TableCell>
                      <Badge className={STATE_BADGE[r.state].cls}>{STATE_BADGE[r.state].label}</Badge>
                      {r.approved_version && <span className="ml-1 text-xs text-gray-500">v{r.approved_version}</span>}
                      {r.open_status === 'submitted' && r.approved_version && <div className="text-xs text-blue-700">v{r.open_version} awaiting approval</div>}
                    </TableCell>
                    <TableCell>
                      {r.state === 'none' ? <span className="text-xs text-gray-400">—</span> : (
                        <div className="flex items-center gap-2"><Progress value={r.completeness} className="h-1.5 w-24" /><span className="text-xs text-gray-600">{r.completeness}%</span></div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.neptune_ready
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Ready for Neptune" />
                        : <XCircle className="h-4 w-4 text-gray-300" aria-label="Not ready for Neptune" />}
                    </TableCell>
                    <TableCell className={cn('text-sm', r.review_due ? 'font-medium text-red-700' : 'text-gray-600')}>
                      {fmtDate(r.reviewed_at)}{r.review_due ? ' (due)' : ''}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-gray-500">
                    <FileText className="mx-auto mb-2 h-6 w-6 text-gray-300" />No roles match.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RoleProfileDialog
        roleId={dialogRole}
        open={dialogRole !== null}
        onOpenChange={(o) => { if (!o) setDialogRole(null); }}
        onChanged={load}
        copySources={summary?.rows}
      />
    </div>
  );
};

export default RoleProfilesTab;
